import React, { useEffect, useState } from "react";
import { EditTwoTone } from "@ant-design/icons";
import { Button, Descriptions, Form, Input, Tag, theme } from "antd";
import { Utils } from "../../Utilities/Utils";
import PracticesService, {
  IGeneralSettingsInitialValues,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { adminContext } from "../../Common/PageRoute";
import { usePractice } from "../../Context/PracticeContext";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";

interface GeneralSettingsProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  generalSettingsData: IGeneralSettingsInitialValues;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
  setPracticeGS: React.Dispatch<
    React.SetStateAction<IGeneralSettingsInitialValues>
  >;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  setLoading,
  isEdit,
  setIsEdit,
  generalSettingsData,
  practiceId,
  setPracticeGS,
}) => {
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const { updatePractice } = usePractice();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const isEditing = isEdit.type === PracticeUpdateTypes.generalSettings;

  useEffect(() => {
    if (generalSettingsData) {
      form.setFieldsValue({
        name: generalSettingsData.name,
        database: generalSettingsData.database,
        host: generalSettingsData.host,
        identifier: generalSettingsData.identifier,
      });
    }
  }, [generalSettingsData, form]);

  const handleOpenEdit = () => {
    if (generalSettingsData) {
      form.setFieldsValue({
        name: generalSettingsData.name,
        database: generalSettingsData.database,
        host: generalSettingsData.host,
        identifier: generalSettingsData.identifier,
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.generalSettings });
  };

  const handleCancel = () => {
    if (generalSettingsData) {
      form.setFieldsValue({
        name: generalSettingsData.name,
        database: generalSettingsData.database,
        host: generalSettingsData.host,
        identifier: generalSettingsData.identifier,
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const OnSaveClick = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setLoading(true);

      const params = {
        ...values,
        connectionString: generalSettingsData?.connectionString || "",
      };

      const res = await PracticesService.updatePracticeGeneralSettings(
        practiceId,
        params,
      );

      if (res?.result) {
        updatePractice({
          label: values.name,
          value: practiceId,
        });
        setPracticeGS((prev) => ({
          ...prev,
          ...values,
        }));
        openNotification("success", "General settings updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
      } else {
        openNotification(
          "error",
          res?.message || "Error updating practice details",
        );
      }
    } catch {
      openNotification("error", "Please fill all mandatory fields.");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: "16px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 12,
          marginBottom: 14,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: token.colorTextHeading,
            }}
          >
            General Settings
          </span>
          <div
            style={{
              fontSize: 11,
              color: token.colorTextSecondary,
              marginTop: 1,
            }}
          >
            Core practice name, database, server host, and tenant identifier.
          </div>
        </div>

        {!isViewOnly && (
          <Button
            size="small"
            type="text"
            shape="circle"
            icon={<EditTwoTone />}
            onClick={handleOpenEdit}
          />
        )}
      </div>

      <Descriptions
        size="small"
        bordered={false}
        column={{ xs: 1, sm: 2, md: 3 }}
        labelStyle={{
          color: token.colorTextSecondary,
          fontWeight: 500,
          fontSize: 12,
        }}
        contentStyle={{
          color: token.colorText,
          fontWeight: 500,
          fontSize: 13,
        }}
      >
        <Descriptions.Item label="Practice Name">
          {generalSettingsData?.name || "—"}
        </Descriptions.Item>

        <Descriptions.Item label="Database Name">
          {generalSettingsData?.database ? (
            <Tag color="cyan" style={{ borderRadius: 4, margin: 0 }}>
              {generalSettingsData.database}
            </Tag>
          ) : (
            "—"
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Identifier">
          {generalSettingsData?.identifier ? (
            <Tag color="geekblue" style={{ borderRadius: 4, margin: 0 }}>
              {generalSettingsData.identifier}
            </Tag>
          ) : (
            "—"
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Host">
          {generalSettingsData?.host || "—"}
        </Descriptions.Item>
      </Descriptions>

      <AppDrawer
        title="Edit General Settings"
        subtitle="Update practice name, database, host, and identifier"
        width={480}
        open={isEditing}
        onClose={handleCancel}
        onSave={OnSaveClick}
        saveText="Save Changes"
        saveButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            label="Practice Name"
            name="name"
            rules={[
              { required: true, message: "Name is required" },
              { validator: Utils.validateWhiteSpace },
            ]}
          >
            <Input
              onInput={(e: any) =>
                (e.target.value =
                  e.target.value.length > 1
                    ? e.target.value
                    : e.target.value.toUpperCase())
              }
              placeholder="Practice Name"
            />
          </Form.Item>

          <Form.Item
            label="Database Name"
            name="database"
            rules={[
              { required: true, message: "Database name is required" },
              { validator: Utils.validateWhiteSpace },
            ]}
          >
            <Input placeholder="Database Name" />
          </Form.Item>

          <Form.Item
            label="Host"
            name="host"
            rules={[
              { required: true, message: "Host is required" },
              { validator: Utils.validateWhiteSpace },
            ]}
          >
            <Input placeholder="e.g. app.domain.com" />
          </Form.Item>

          <Form.Item
            label="Identifier"
            name="identifier"
            rules={[
              { required: true, message: "Identifier is required" },
              { validator: Utils.validateWhiteSpace },
            ]}
          >
            <Input placeholder="Practice identifier" />
          </Form.Item>
        </Form>
      </AppDrawer>
    </div>
  );
};

export default GeneralSettings;
